import { useImagesByTags } from "@/hooks/useImages";
import { useState, useEffect, useCallback, useMemo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useIsMobile } from "@/hooks/use-mobile";
import SwipeHint from "@/components/ui/SwipeHint";
import { SectionHeader } from "@/components/ui/SectionHeader";

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

interface GalleryImage {
  image?: string;
  caption: string;
  location: string;
}

interface GallerySectionProps {
  images?: GalleryImage[];
  tagSlug?: string;
  tagSlugs?: string[];
  headline?: string;
  subheadline?: string;
  eyebrow?: string;
}

const GallerySection = ({
  images: providedImages,
  tagSlug,
  tagSlugs,
  headline = "See Our Work in Action",
  subheadline = "Every roof tells a story. Browse real projects completed by our team across the Greater Houston area, from storm damage repairs to complete replacements.",
  eyebrow = "OUR PORTFOLIO"
}: GallerySectionProps = {}) => {
  const isMobile = useIsMobile();
  const tagsToFetch = tagSlugs || (tagSlug ? [tagSlug] : []);
  const { data: taggedImages } = useImagesByTags(tagsToFetch);

  // Convert tagged images to GalleryImage format and shuffle them for random display order
  const convertedTaggedImages: GalleryImage[] = useMemo(() => {
    const converted = taggedImages?.map(img => ({
      image: img.file_url,
      caption: img.filename.replace(/\.[^/.]+$/, "").replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
      location: "Houston Area"
    })) || [];

    // Shuffle the images so they display in random order instead of chronological
    return shuffleArray(converted);
  }, [taggedImages]);

  // Priority: manual images > tagged images
  const images = providedImages || convertedTaggedImages;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [selectedBatch, setSelectedBatch] = useState(0);
  const [selectedMobileIndex, setSelectedMobileIndex] = useState(0);

  // Separate Embla carousels for desktop and mobile
  const [desktopEmblaRef, desktopEmblaApi] = useEmblaCarousel({ 
    loop: true,
    align: "start",
    skipSnaps: false
  });
  
  const [mobileEmblaRef, mobileEmblaApi] = useEmblaCarousel({ 
    loop: true,
    align: "center",
  });

  // Filter out images that failed to load
  const validImages = images.filter(img => img.image && !failedImages.has(img.image));

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImages(prev => new Set(prev).add(imageUrl));
  }, []);

  // Configuration - 4 images per batch for compact layout
  const IMAGES_PER_BATCH = 4;
  const ROTATION_INTERVAL = 8000; // 8 seconds (slower)
  const totalBatches = Math.ceil(validImages.length / IMAGES_PER_BATCH);

  // Create batches of images
  const batches = Array.from({ length: totalBatches }, (_, i) => 
    validImages.slice(i * IMAGES_PER_BATCH, (i + 1) * IMAGES_PER_BATCH)
  );

  const handleImageClick = (batchIndex: number, imageIndexInBatch: number) => {
    // For mobile, batchIndex is 0 and imageIndexInBatch is the actual index
    // For desktop, calculate global index
    const globalIndex = isMobile ? imageIndexInBatch : batchIndex * IMAGES_PER_BATCH + imageIndexInBatch;
    setCurrentImageIndex(globalIndex);
    setLightboxOpen(true);
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
  };

  const handlePrev = () => {
    setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
  };

  const handleClose = () => {
    setLightboxOpen(false);
  };

  // Sync desktop carousel with selected batch
  useEffect(() => {
    if (desktopEmblaApi) {
      desktopEmblaApi.on("select", () => {
        setSelectedBatch(desktopEmblaApi.selectedScrollSnap());
      });
    }
  }, [desktopEmblaApi]);

  // Sync mobile carousel with selected index
  useEffect(() => {
    if (mobileEmblaApi) {
      mobileEmblaApi.on("select", () => {
        setSelectedMobileIndex(mobileEmblaApi.selectedScrollSnap());
      });
    }
  }, [mobileEmblaApi]);

  // Auto-rotation with Embla (desktop only)
  useEffect(() => {
    if (!desktopEmblaApi || totalBatches <= 1) return;

    const interval = setInterval(() => {
      desktopEmblaApi.scrollNext();
    }, ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [desktopEmblaApi, totalBatches]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, currentImageIndex, validImages.length]);

  // Touch swipe for lightbox
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNext();
      else handlePrev();
    }
    setTouchStart(null);
  };

  // Don't render if no valid images available
  if (!validImages || validImages.length === 0) {
    return null;
  }

  return (
    <>
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow={eyebrow}
            headline={headline}
            subheadline={subheadline}
            className="mb-16"
          />

          {/* Desktop: 2x2 Grid Carousel */}
          <div className="hidden md:block">
            <div className="overflow-hidden rounded-lg" ref={desktopEmblaRef}>
              <div className="flex">
                {batches.map((batch, batchIndex) => (
                  <div 
                    key={batchIndex} 
                    className="flex-[0_0_100%] min-w-0"
                  >
                    {/* 2x2 Grid */}
                    <div className="grid grid-cols-4 gap-4">
                      {batch.map((image, imageIndex) => (
                        <div
                          key={imageIndex}
                          className="cursor-pointer group"
                          onClick={() => image.image && handleImageClick(batchIndex, imageIndex)}
                        >
                          <div className="rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-muted">
                            <div className="aspect-[16/10] relative overflow-hidden">
                              {image.image ? (
                                <img
                                  src={image.image}
                                  alt={image.caption}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  onError={() => image.image && handleImageError(image.image)}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-muted">
                                  <span className="text-sm text-muted-foreground">Coming Soon</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop Navigation */}
            {totalBatches > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6">
                <button
                  onClick={() => desktopEmblaApi?.scrollPrev()}
                  className="rounded-block bg-navy/10 p-2 transition-colors hover:bg-navy/20"
                  aria-label="Previous batch"
                >
                  <ChevronLeft className="h-5 w-5 text-navy" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: totalBatches }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => desktopEmblaApi?.scrollTo(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === selectedBatch
                          ? "w-8 bg-primary"
                          : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                      }`}
                      aria-label={`Go to batch ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => desktopEmblaApi?.scrollNext()}
                  className="rounded-block bg-navy/10 p-2 transition-colors hover:bg-navy/20"
                  aria-label="Next batch"
                >
                  <ChevronRight className="h-5 w-5 text-navy" />
                </button>
              </div>
            )}
          </div>

          {/* Mobile: Single image swipeable carousel (like testimonials) */}
          <div className="md:hidden relative">
            {/* Swipe Hint */}
            <SwipeHint />
            
            {/* Edge gradients to hint more content */}
            <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-accent/50 to-transparent z-[5] pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-accent/50 to-transparent z-[5] pointer-events-none" />
            
            <div className="overflow-hidden rounded-lg" ref={mobileEmblaRef}>
              <div className="flex">
                {validImages.map((image, index) => (
                  <div 
                    key={index} 
                    className="flex-[0_0_100%] min-w-0 px-4"
                  >
                    <div
                      className="cursor-pointer group"
                      onClick={() => image.image && handleImageClick(0, index)}
                    >
                      <div className="rounded-lg overflow-hidden shadow-md bg-muted">
                        <div className="aspect-[4/3] relative overflow-hidden">
                          {image.image ? (
                            <img
                              src={image.image}
                              alt={image.caption}
                              loading="lazy"
                              className="w-full h-full object-cover"
                              onError={() => image.image && handleImageError(image.image)}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <span className="text-sm text-muted-foreground">Coming Soon</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Mobile dot indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {validImages.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === selectedMobileIndex ? 'bg-primary w-8' : 'bg-muted-foreground/30'
                  }`}
                  onClick={() => mobileEmblaApi?.scrollTo(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal with Touch Swipe */}
      {lightboxOpen && validImages[currentImageIndex]?.image && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={handleClose}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            onClick={handleClose}
            aria-label="Close lightbox"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 text-white text-sm font-medium bg-black/50 px-3 py-2 rounded-full">
            {currentImageIndex + 1} / {validImages.length}
          </div>

          {/* Previous Button */}
          {validImages.length > 1 && (
            <button
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6 md:h-8 md:w-8 text-white" />
            </button>
          )}

          {/* Image with smooth transition */}
          <div 
            className="max-w-5xl max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={validImages[currentImageIndex].image}
              alt={validImages[currentImageIndex].caption}
              className="max-w-full max-h-[85vh] object-contain rounded-lg transition-opacity duration-300"
            />
          </div>

          {/* Next Button */}
          {validImages.length > 1 && (
            <button
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6 md:h-8 md:w-8 text-white" />
            </button>
          )}

          {/* Swipe hint on mobile */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs md:hidden">
            Swipe to navigate
          </div>
        </div>
      )}
    </>
  );
};

export default GallerySection;
